import { useClickOutside } from '../../hooks/useClickOutside'
import { createContext, useCallback, useContext, useState, type PropsWithChildren, type ReactElement } from 'react'
import ReactModal from 'react-modal'
import { useNavigate } from 'react-router-dom'
import { type ModalConfig, type ModalContextValue } from './modal-context.props'
import * as S from './modal-context.styled'
import close from './assets/close.svg'

const ModalContext = createContext<ModalContextValue | null>(null)

export function ModalProvider ({ children }: PropsWithChildren): ReactElement {
  const navigate = useNavigate()
  const [modalConfig, setModalConfig] = useState<ModalConfig>({} as unknown as ModalConfig)
  const [isOpen, setIsOpen] = useState(false)
  const modalRef = useClickOutside(() => {
    if (modalConfig.useClickOutside) closeModal()
  })

  const closeModal = useCallback((callback?: () => void) => {
    setIsOpen(false)
    if (callback != null && modalRef.current != null) {
      modalRef.current.addEventListener('animationend', callback, { once: true })
    }

    if (modalConfig.goTo) navigate(modalConfig.goTo)
  }, [modalConfig.goTo])

  const openModal = useCallback(({ hasCloseButton = true, useClickOutside = true, ...config }: ModalConfig) => {
    setIsOpen(true)
    ReactModal.setAppElement('#root')
    setModalConfig({ ...config, hasCloseButton, useClickOutside })
  }, [])

  const modalContextValue: ModalContextValue = {
    closeModal,
    openModal
  }

  return <ModalContext.Provider value={modalContextValue}>
    {children}
    <S.Modal
      className={modalConfig.className ? modalConfig.className : ''}
      isOpen={isOpen}
      onRequestClose={() => closeModal}
      style={{
        content: modalConfig.styles,
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 9999
        }
      }}
    >
      <div ref={modalRef}>{modalConfig.body}</div>
      {modalConfig.hasCloseButton && <button onClick={() => { closeModal() }}>
        Fechar
        {' '}
        <img src={close} alt="Fechar" />
      </button>}
    </S.Modal>
  </ModalContext.Provider>
}

export function useModal (): ModalContextValue {
  const context = useContext(ModalContext)

  if (context == null) throw new Error('useModal must be used within a ModalProvider')

  return context
}
